/**
 * PathfindingSystem.ts
 * A* pathfinding via pathfinding.js over the tile grid (§9.3).
 *
 * Grid is built from TileMap.isPassable(). Impassable tiles (buildings,
 * water, locked) become blocked nodes.
 *
 * Optimisation: a single `workGrid` is kept alive and its A* traversal state
 * is reset in-place after every search (field writes on existing Node objects).
 * This avoids the W×H Node allocations that `baseGrid.clone()` would trigger
 * on every findPath call — critical as the map grows larger.
 *
 * Per-frame budget: beginFrame() resets a counter. findPath() decrements it.
 * hasPathBudget() lets callers skip low-priority re-paths when the budget is
 * exhausted. Player-click paths always go through regardless.
 *
 * Async queue: requestPath(from, to, callback) enqueues a deferred request.
 * flushQueue(budget) drains up to `budget` items and fires callbacks. Call
 * once per game-loop frame to spread A* cost across frames.
 *
 * Group movement: formationTargets() returns a set of passable tiles
 * arranged outward from the target so units spread naturally instead
 * of stacking on one tile. The closest unit gets the center position.
 */

import PF from 'pathfinding';
import type { TileMap } from '../map/TileMap';
import type { GridPosition } from '../types';
import { isMobileDevice } from '../mapConfig';

/** Spiral offsets expanding outward from a centre tile. */
const FORMATION_OFFSETS: [number, number][] = [
  [0,  0],
  [1,  0], [-1,  0], [0,  1], [0, -1],
  [1,  1], [-1,  1], [1, -1], [-1, -1],
  [2,  0], [-2,  0], [0,  2], [0, -2],
  [2,  1], [-2,  1], [1,  2], [-1,  2],
];

/** Internal shape of a pathfinding.js Node (not exported by the library). */
interface PFNode {
  walkable: boolean;
  opened:   boolean;
  closed:   boolean;
  g:        number;
  h:        number;
  f:        number;
  parent:   unknown;
}

interface PathRequest {
  from:         GridPosition;
  to:           GridPosition;
  extraBlocked?: GridPosition[];
  callback:     (path: GridPosition[]) => void;
}

// How many A* searches to allow per frame before lower-priority requests queue.
const FRAME_BUDGET     = isMobileDevice() ? 4 : 10;
// How many queued requests to drain per frame.
const QUEUE_DRAIN      = isMobileDevice() ? 2 : 5;

export class PathfindingSystem {
  /** Persistent grid — rebuilt only when terrain changes, never cloned. */
  private workGrid!: InstanceType<typeof PF.Grid>;
  /** Reused finder instance — stateless between findPath calls. */
  private readonly finder = new PF.AStarFinder({
    diagonalMovement: PF.DiagonalMovement.OnlyWhenNoObstacles,
  });

  /** Per-frame budget — decremented by findPath(), reset by beginFrame(). */
  private frameBudget = FRAME_BUDGET;
  /** Deferred low-priority path requests. */
  private readonly queue: PathRequest[] = [];

  constructor(private readonly map: TileMap) {
    this.rebuildGrid();
  }

  /**
   * Call once at the start of each game-loop frame.
   * Resets the per-frame path budget and drains the deferred queue.
   */
  beginFrame(): void {
    this.frameBudget = FRAME_BUDGET;
    // Drain the async queue — up to QUEUE_DRAIN entries per frame.
    let drained = 0;
    while (this.queue.length > 0 && drained < QUEUE_DRAIN) {
      const req = this.queue.shift()!;
      req.callback(this.findPath(req.from, req.to, req.extraBlocked));
      drained++;
    }
  }

  /**
   * True while the per-frame path budget is not exhausted.
   * Low-priority callers should skip re-pathing when this returns false.
   */
  hasPathBudget(): boolean {
    return this.frameBudget > 0;
  }

  /**
   * Enqueue a deferred path request. The callback fires (with the result) in a
   * future frame when the queue is drained. Good for AI / auto-movement that
   * can tolerate a 1–2 frame delay. Player-ordered movement should use
   * findPath() directly for immediate response.
   */
  requestPath(
    from: GridPosition,
    to: GridPosition,
    callback: (path: GridPosition[]) => void,
    extraBlocked?: GridPosition[],
  ): void {
    this.queue.push({ from, to, extraBlocked, callback });
  }

  /**
   * Rebuild the walkability grid from the current map terrain.
   * Call after any terrain change (building placed, area unlocked, etc).
   * Also clears the deferred queue to prevent stale paths.
   */
  rebuildGrid(): void {
    const matrix: number[][] = [];
    for (let row = 0; row < this.map.height; row++) {
      matrix[row] = [];
      for (let col = 0; col < this.map.width; col++) {
        matrix[row][col] = this.map.isPassable({ col, row }) ? 0 : 1;
      }
    }
    this.workGrid = new PF.Grid(matrix);
    this.queue.length = 0; // stale requests reference old terrain
  }

  /**
   * Find a path from `from` to `to`.
   * Returns path as grid positions EXCLUDING the start tile.
   * Returns [] if no path exists or start === end.
   * Decrements the per-frame budget; use hasPathBudget() to guard low-priority callers.
   */
  findPath(from: GridPosition, to: GridPosition, extraBlocked?: GridPosition[]): GridPosition[] {
    this.frameBudget--;

    if (from.col === to.col && from.row === to.row) return [];

    const target   = this.clamp(to);
    const resolved = this.map.isPassable(target)
      ? target
      : this.nearestPassable(target);
    if (!resolved) return [];

    // Temporarily mark extra-blocked tiles (occupied by other units).
    if (extraBlocked) {
      for (const pos of extraBlocked) {
        if (this.map.inBounds(pos.col, pos.row)) {
          this.workGrid.setWalkableAt(pos.col, pos.row, false);
        }
      }
    }

    const raw = this.finder.findPath(
      from.col, from.row,
      resolved.col, resolved.row,
      this.workGrid,
    );

    // Reset traversal state and restore walkability — no allocations.
    this.resetWorkGrid(extraBlocked);

    // raw = [[col, row], ...]; skip index 0 (start tile)
    return raw.slice(1).map(([col, row]) => ({ col, row }));
  }

  /**
   * Return formation target tiles for `count` units moving toward `target`.
   * Tiles spiral outward from centre; each is passable and in-bounds.
   * Index 0 → centre (assign to closest unit).
   */
  formationTargets(target: GridPosition, count: number): GridPosition[] {
    const result: GridPosition[] = [];
    for (const [dc, dr] of FORMATION_OFFSETS) {
      if (result.length >= count) break;
      const pos = { col: target.col + dc, row: target.row + dr };
      if (this.map.inBounds(pos.col, pos.row) && this.map.isPassable(pos)) {
        result.push(pos);
      }
    }
    while (result.length < count) result.push({ ...target });
    return result;
  }

  // ── Helpers ─────────────────────────────────────────────────────────────

  private clamp(pos: GridPosition): GridPosition {
    return {
      col: Math.max(0, Math.min(this.map.width  - 1, pos.col)),
      row: Math.max(0, Math.min(this.map.height - 1, pos.row)),
    };
  }

  private nearestPassable(pos: GridPosition): GridPosition | null {
    for (const [dc, dr] of FORMATION_OFFSETS) {
      const p = { col: pos.col + dc, row: pos.row + dr };
      if (this.map.inBounds(p.col, p.row) && this.map.isPassable(p)) return p;
    }
    return null;
  }

  /**
   * Reset A* traversal state on all nodes without allocating.
   * Restores any extra-blocked tiles first, then clears opened/closed/scores.
   */
  private resetWorkGrid(extraBlocked?: GridPosition[]): void {
    if (extraBlocked) {
      for (const pos of extraBlocked) {
        if (this.map.inBounds(pos.col, pos.row)) {
          this.workGrid.setWalkableAt(pos.col, pos.row, true);
        }
      }
    }
    const nodes = (this.workGrid as unknown as { nodes: PFNode[][] }).nodes;
    for (const row of nodes) {
      for (const node of row) {
        node.opened = false;
        node.closed = false;
        node.g      = 0;
        node.h      = 0;
        node.f      = 0;
        node.parent = null;
      }
    }
  }
}
